import { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate, replace } from 'react-router-dom'
import { CheckCircle2, Minus, Plus, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLoading } from '../providers/loading'
import diagramApi from '../api/diagram'
import productApi from '../api/product'
import Select from '../components/select'
import ImageEditor from '../components/image-editor'
import Modal from '../components/modal'
import Button from '../components/button'
import SearchPage from './search'
import CheckImage from '../assets/images/check'

const formatPrice = (price) => {
  return price ? price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }) : '-'
}

const ScrollableCardContainer = ({ children }) => {
  const [showLeftGradient, setShowLeftGradient] = useState(false)
  const [showRightGradient, setShowRightGradient] = useState(false)
  const containerRef = useRef(null)

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current
      setShowLeftGradient(scrollLeft > 0)
      setShowRightGradient(scrollLeft + clientWidth < scrollWidth - 1) // -1 to account for rounding
    }
  }

  useEffect(() => {
    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [])

  const scrollLeft = () => {
    if (containerRef.current) {
      // Scroll left by 300px or a different value as needed
      containerRef.current.scrollBy({ left: -300, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (containerRef.current) {
      // Scroll right by 300px or a different value as needed
      containerRef.current.scrollBy({ left: 300, behavior: 'smooth' })
    }
  }

  return (
    <div className="relative">
      {/* Left navigation arrow */}
      {showLeftGradient && (
        <button 
          onClick={scrollLeft} 
          className="absolute left-2 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-yellow-400 rounded-full shadow-md hover:bg-yellow-500 transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6 text-black" />
        </button>
      )}

      {/* Right navigation arrow */}
      {showRightGradient && (
        <button 
          onClick={scrollRight} 
          className="absolute right-2 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-yellow-400 rounded-full shadow-md hover:bg-yellow-500 transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6 text-black" />
        </button>
      )}

      {/* Gradient on the left side */}
      {showLeftGradient && (
        <div className="absolute left-0 top-0 bottom-4 w-32 bg-gradient-to-r from-[#F5F5F5] to-transparent z-10 pointer-events-none" />
      )}

      {/* Gradient on the right side */}
      {showRightGradient && (
        <div className="absolute right-0 top-0 bottom-4 w-32 bg-gradient-to-l from-[#F5F5F5] to-transparent z-10 pointer-events-none" />
      )}

      <div
        ref={containerRef}
        className="overflow-x-auto w-0 min-w-full"
        onScroll={checkScroll}
      >
        <div className="flex gap-[20px] pb-4">
          {children}
        </div>
      </div>
    </div>
  )
}

const QtyBlock = ({
  qty = 1,
  onDecrease = () => {},
  onIncrease = () => {}
}) => {
  return (
    <div className="w-[90px] border border-[#252525] p-[6px] flex items-center justify-between gap-[4px]">
      <Minus
        className="cursor-pointer"
        onClick={onDecrease}
      />
      {qty}
      <Plus
        className="cursor-pointer"
        onClick={onIncrease}
      />
    </div>
  )
}

const ShoppingCardBlock = ({
  onClick = (e) => {},
  qty
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = (e) => {
    if (isLoading) {
      return
    }

    onClick(e)
    setIsLoading(true)

    setTimeout(() => {
      setIsLoading(false)
    }, 3000)
  }

  return (
    <div
      className="flex items-center justify-center bg-yellow-400 w-fit py-2 px-5 cursor-pointer"
      onClick={handleClick}
      style={{ opacity: isLoading ? 0.7 : 1 }}
    >
      {isLoading ? (
        <svg className="animate-spin h-[24px] w-[33px]" viewBox="0 0 24 24">
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
            fill="none"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <div className='flex items-center'>
          +<ShoppingCart />
          <span class='ml-2 font-bold text-base'>Add{qty ? ` (${qty})` : ''} to Cart</span>
        </div>
      )}
    </div>
  )
}

const ProductBlock = ({ 
  product, 
  index = 0,
  level = 0,
  fullLevelLine = false,
  selectedProduct,
  onSelect,
  onIncreaseQty,
  onDecreaseQty,
  onViewDetails,
  parentName
}) => {
  const isDiscontinued = !product.product

  const hasReplacements = Boolean(
    product.has_replacement_options ??
    product.product?.has_replacement_options ??
    (Array.isArray(product.replacement_options) && product.replacement_options.length > 0) ??
    product.replacement_options_count ??
    product.shopify_id
  )

  const marginLeft = `${level * 20}px`
  const renderProduct = (product) => ( 
    <div className="relative">
      <div className={'relative'} style={{marginLeft}}>
        {level > 0 && (
          <>
            <div className="absolute -left-3 top-1/2 w-3 border-t-[3px] border-solid border-[#21A71E]" />
            <div
              className="absolute -left-3 top-0 border-l-[3px] border-solid border-[#21A71E]"
              style={fullLevelLine ? { height: '100%' } : { height: '50%' }}
            />
          </>
        )}

        <div
          data-product-id={product._id}
          className={`flex w-full flex-row flex-wrap md:flex-nowrap text-sm font-normal text-black md:overflow-x-auto ${
            product._id === selectedProduct?._id ? 'border-2 border-[#21A71E]' : 'border border-[#D9D9D9]'
          } ${
            product.highlight ? 'bg-[#F9F9F9]' : 'bg-[#FFFFFF]'
          }`}
          onClick={() => onSelect(product)}
        >
          <div className={`flex w-full flex-row ${isDiscontinued ? 'opacity-60' : ''}`}>
            <div className="px-[15px] md:px-[20px] py-[13px] md:w-[170px] shrink-0">
              <div className="flex justify-between">
                <span className='hidden md:block'>{product.related_number ?? '-'}</span>
                <span className='block md:hidden'>#{product.related_number ?? '-'}</span>
                <img src={product?.product?.image_url} className="w-[100px] h-[100px] hidden md:block object-cover" alt="" />
              </div>
            </div>
            
            <div className="px-[1px] md:px-[20px] py-[13px] flex-grow">
              <div className="flex flex-col gap-[4px]">
                <b>{product.product_name}</b>
                <p>{product.sku}</p>
                <p>Recommended Qty: {product.recommended_qty}</p>
                {(() => {
                  const includedText = level > 0 ? `Included in ${parentName}` : null
                  const otherNotes = product.notes?.trim() || ''

                  // Combine child info + any existing notes
                  const combined = [includedText, otherNotes]
                    .filter(Boolean)
                    .join('; ')

                  // Only render the notes box if something to show
                  if (!combined) return null

                  return (
                    <div className="font-bold bg-[#fff2cc] px-1.5 max-w-fit">
                      Notes: {combined}
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>

          {/* RIGHT column (price/ATC OR DISCONTINUED) */}
          <div className="px-[20px] py-[13px] shrink-0 w-full md:w-max">
            {isDiscontinued ? (
              // DISCONTINUED: replace price/qty/ATC with a clear tag
              <div className="flex flex-col gap-[10px] items-end">
                <div className="uppercase tracking-wide text-xs font-bold text-[#8a8a8a]">
                  DISCONTINUED
                </div>

                {/* Discontinued: show details/replacements ONLY if they exist */}
                {hasReplacements && (
                  <>
                    <div
                      className="text-[#005B75] underline cursor-pointer hidden md:block"
                      onClick={(e) => {
                        e.stopPropagation()
                        onViewDetails(product._id)
                      }}
                    >
                      View Details/Replacements
                    </div>
                    <div className="block md:hidden">
                      <Button
                        text="View Details/Replacements"
                        onClick={() => onViewDetails(product._id)}
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              // AVAILABLE: normal price/qty/ATC + ALWAYS show the details link (desktop + mobile)
              product.product && (
                <div className="flex flex-col gap-[10px] items-end">
                  <div className="flex gap-2 items-center">
                    <div className="line-through">${formatPrice(product.product.regular_price)}</div>
                    <div className="text-lg font-bold">
                      ${formatPrice(product.product.sale_price)} ea.
                    </div>
                  </div>

                  <div className="gap-[15px] items-center hidden md:flex">
                    <QtyBlock
                      qty={product.qty}
                      onDecrease={(e) => {
                        e.stopPropagation()
                        onDecreaseQty(product._id)
                      }}
                      onIncrease={(e) => {
                        e.stopPropagation()
                        onIncreaseQty(product._id)
                      }}
                    />
                    <ShoppingCardBlock
                      qty={product.qty}
                      onClick={(e) => {
                        e.stopPropagation()
                        window.parent.postMessage({
                          action: 'storeToBucket',
                          data: { productId: product.shopify_id, qty: product.qty }
                        }, '*')
                      }}
                    />
                  </div>

                  {/* Available: always show the link */}
                  <div
                    className="text-[#005B75] underline cursor-pointer hidden md:block"
                    onClick={(e) => {
                      e.stopPropagation()
                      onViewDetails(product._id)
                    }}
                  >
                    View Details/Replacements
                  </div>
                  <div className="block md:hidden">
                    <Button
                      text="View Details/Replacements"
                      onClick={() => onViewDetails(product._id)}
                    />
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {product.children && product.children.length > 0 && (
        <div className="relative">
          {product.children.map((child, childIndex) => (
            <ProductBlock
              key={child._id}
              index={index}
              product={child}
              level={level + 1}
              fullLevelLine={
                childIndex !== product.children.length - 1
              }
              selectedProduct={selectedProduct}
              onSelect={onSelect}
              onIncreaseQty={onIncreaseQty}
              onDecreaseQty={onDecreaseQty}
              onViewDetails={onViewDetails}
              parentName={product.product_name}
            />
          ))}
        </div>
      )}
    </div>
  )

  return renderProduct(product)
}

const DetailedProductBlock = ({ 
  product,
  onIncreaseQty,
  onDecreaseQty
}) => {
  const addQty = product.qty ?? product.recommended_qty ?? 1
  const renderProduct = (product) => ( 
    <div className="relative">
      <div className={'relative'}>
        <div
          className={`flex w-full flex-wrap md:flex-nowrap text-sm font-normal text-black bg-white md:bg-[#F9F9F9] md:overflow-x-auto`}
        >
          <div className='flex w-full flex-row'>
            <div className="md:px-[20px] md:py-[13px] w-[130px] h-[130px] md:w-[210px] md:h-[210px] shrink-0">
              <img src={product?.product?.image_url} className="w-full h-full object-cover" alt="" />
            </div>
            
            <div className="px-[20px] py-[13px] md:flex-grow">
              <div className="flex flex-col gap-[4px]">
                <b>{product.product_name}</b>
                <div className="flex md:hidden gap-2 items-center">
                  <div className="line-through">${formatPrice(product.product.regular_price)}</div>
                  <div className="font-bold">
                    ${formatPrice(product.product.sale_price)} ea.
                  </div>
                </div>
                <p className='hidden md:block'>SKU: {product.sku}</p>
                <p className='hidden md:block'>Recommended Qty: {product.recommended_qty}</p>
                {product.notes && <b className='hidden md:block'>Notes: {product.notes}</b>}
              </div>
            </div>
          </div>

          <div className='flex md:hidden flex-col gap-2 mt-2'>
            <p>{product.sku}</p>
            <p>Recommended Qty: {product.recommended_qty}</p>
            {product.notes && <b>Notes: {product.notes}</b>}
          </div>

          {product.product?.product_handle && (
            <div className='flex md:hidden mt-2 w-full justify-end'>
              <a className='underline text-blue-900' href={`${process.env.REACT_APP_SHOP_ORIGIN}/products/${product.product?.product_handle}`} target='_top' rel='noopener noreferrer'>
                View details
              </a>
            </div>
          )}

          <div className="md:px-[20px] py-[13px] shrink-0 flex self-center justify-center md:justify-end md:self-end w-full md:w-max">
            {product.product && (
              <div className="flex flex-col gap-[10px] items-end w-full">
                <div className="hidden md:flex gap-2 items-center">
                  <div className="line-through">${formatPrice(product.product.regular_price)}</div>
                  <div className="text-lg font-bold">
                    ${formatPrice(product.product.sale_price)} ea.
                  </div>
                </div>
                <div className="flex gap-[15px] w-full items-center">
                  <QtyBlock
                    qty={product.qty}
                    onDecrease={(e) => {
                      e.stopPropagation()
                      onDecreaseQty(product._id)
                    }}
                    onIncrease={(e) => {
                      e.stopPropagation()
                      onIncreaseQty(product._id)
                    }}
                  />
                  <div className='block md:hidden flex-grow'>
                    <Button
                      text={`Add (${addQty}) to Cart`}
                      enableLoader
                      onClick={() => {
                        window.parent.postMessage({
                          'action': 'storeToBucket',
                          'data': {
                            'productId': product.shopify_id,
                            'qty': product.qty
                          }
                        }, '*')
                      }}
                    />
                  </div>
                  <div className='hidden md:block'>
                    <ShoppingCardBlock
                      qty={product.qty}
                      onClick={(e) => {
                        e.stopPropagation()
                        window.parent.postMessage({
                          'action': 'storeToBucket',
                          'data': {
                            'productId': product.shopify_id,
                            'qty': product.qty
                          }
                        }, '*')
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return renderProduct(product)
}

const DetailedReplacementOption = ({ 
  product,
  onIncreaseQty,
  onDecreaseQty
}) => {
  const addQty = product.qty ?? 1
  const renderProduct = (product) => ( 
    <div className="relative">
      <div className={'relative'}>
        <div
          className={`px-[10px] py-[7px] flex w-full flex-wrap md:flex-nowrap text-sm font-normal text-black bg-[#F5F5F5] md:bg-[#F9F9F9] md:overflow-x-auto`}
        >
          <div className='flex w-full flex-row'>
            <div className="w-[130px] h-[130px] md:w-[210px] md:h-[210px] shrink-0">
              <img src={product.attached_product?.image_url} className="w-full h-full object-cover" alt="" />
            </div>
            
            <div className="px-[20px] py-[13px] md:flex-grow">
              <div className="flex flex-col gap-[4px]">
                <b>{product.attached_product?.name}</b>
                <div className="flex md:hidden gap-2 items-center">
                  {product.attached_product?.regular_price && <div className="line-through">${formatPrice(product.attached_product?.regular_price)}</div>}
                  <div className="font-bold">
                    ${formatPrice(product.attached_product?.sale_price)} ea.
                  </div>
                </div>
                <p className='hidden md:block'>SKU: {product.attached_product.sku}</p>
                {product.notes && <b className='hidden md:block'>Notes: {product.attached_product.notes}</b>}
              </div>
            </div>
          </div>

          <div className='flex md:hidden flex-col gap-2 mt-2'>
            <p>{product.attached_product.sku}</p>
            {product.notes && <b>Notes: {product.attached_product.notes}</b>}
          </div>

          {product.attached_product?.product_handle && (
            <div className='flex md:hidden mt-2 w-full justify-end'>
              <a className='underline text-blue-900' href={`${process.env.REACT_APP_SHOP_ORIGIN}/products/${product.attached_product?.product_handle}`} target='_top' rel='noopener noreferrer'>
                View details
              </a>
            </div>
          )}

          <div className="md:px-[20px] py-[13px] shrink-0 flex self-center justify-center md:justify-end md:self-end w-full md:w-max">
            <div className="flex flex-col gap-[10px] items-end w-full">
              <div className="hidden md:flex gap-2 items-center">
                {product.attached_product?.regular_price && <div className="line-through">${formatPrice(product.attached_product?.regular_price)}</div>}
                <div className="text-lg font-bold">
                  ${formatPrice(product.attached_product?.sale_price)} ea.
                </div>
              </div>
              <div className="flex gap-[15px] w-full items-center">
                <QtyBlock
                  qty={product.qty}
                  onDecrease={(e) => {
                    e.stopPropagation()
                    onDecreaseQty(product._id)
                  }}
                  onIncrease={(e) => {
                    e.stopPropagation()
                    onIncreaseQty(product._id)
                  }}
                />
                <div className='block md:hidden flex-grow'>
                  <Button
                    text={`Add (${addQty}) to Cart`}
                    enableLoader
                    onClick={() => {
                      window.parent.postMessage({
                        'action': 'storeToBucket',
                        'data': {
                          'productId': product.attached_product?.shopify_id,
                          'qty': product.qty
                        }
                      }, '*')
                    }}
                  />
                </div>
                <div className='hidden md:block'>
                  <ShoppingCardBlock
                    qty={product.qty}
                    onClick={(e) => {
                      e.stopPropagation()
                      window.parent.postMessage({
                        'action': 'storeToBucket',
                        'data': {
                          'productId': product.attached_product?.shopify_id,
                          'qty': product.qty
                        }
                      }, '*')
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return renderProduct(product)
}

const DiagramPage = () => {
  const [searchParams] = useSearchParams()
  const { setIsLoading } = useLoading()
  const navigate = useNavigate()

  const [diagram, setDiagram] = useState(null)
  const [allProducts, setAllProducts] = useState([])
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [points, setPoints] = useState([])
  const [image, setImage] = useState(null)
  const [diagramTitle, setDiagramTitle] = useState('')

  const [displayLimit, setDisplayLimit] = useState(10)
  const [expandedAll, setExpandedAll] = useState(false)

  const engineType = searchParams.get('engine_type')
  const enginePower = searchParams.get('engine_power')
  const model = searchParams.get('model')
  const year = searchParams.get('year')
  const category = searchParams.get('category')
  const selectedProductId = searchParams.get('selected_product_id')

  const [selectedCategory, setSelectedCategory] = useState(category)
  const containerRef = useRef(null)
  const [showBottomGradient, setShowBottomGradient] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedProductDetails, setSelectedProductDetails] = useState(null)

  const [isDifferentEngineModalOpen, setIsDifferentEngineModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)

  const [detailedProduct, setDetailedProduct] = useState(null)

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current
      setShowBottomGradient(Math.ceil(scrollTop + clientHeight) < scrollHeight)
    }
  }

  const fetchDiagram = async () => {
    setIsLoading(true)

    try {
      const response = await diagramApi.search({
        engine_type: engineType,
        engine_power: enginePower,
        model,
        year,
        category
      })

      if (response?.success && response.data) {
        const data = response.data

        setDiagram(data.diagram)
        setDiagramTitle(data.diagramTitle)
        setSelectedProduct(null)
        setSelectedProductDetails(null)

        const hasAwsCreds =
          !!process.env.REACT_APP_AWS_ACCESS_KEY_ID &&
          !!process.env.REACT_APP_AWS_SECRET_ACCESS_KEY &&
          !!process.env.REACT_APP_AWS_REGION;

        if (data.diagram?.image_url) {
          setImage({ file: null, preview: data.diagram.image_url });
        } else if (process.env.REACT_APP_IS_LOCAL === 'true' && !hasAwsCreds) {
          console.log('setting dev image');
          setImage({ file: null, preview: 'https://placehold.co/1200x1600/png' });
        }

        if (data.categories && Array.isArray(data.categories)) {
          setCategories(data.categories)
        }

        if (data.products && Array.isArray(data.products)) {
          setAllProducts(data.products)

          const points = data.products.filter(product => product.related_number).map(product => ({
            x: product.x_axys,
            y: product.y_axys,
            number: product.related_number,
            size: product.size,
            is_hidden: product.is_hidden
          }))
  
          setProducts(buildProductTree(data.products))
          setPoints(points)
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    const searchParams = new URLSearchParams()

    searchParams.append('engine_type', engineType)
    searchParams.append('engine_power', enginePower)
    searchParams.append('model', model)
    searchParams.append('year', year)
    searchParams.append('category', 'all')

    window.parent.postMessage({
      'action': 'redirect',
      'data': {
        'url': '/diagrams',
        'search': searchParams.toString()
      }
    }, '*')

    // navigate({
    //   pathname: '/diagrams',
    //   search: searchParams.toString()
    // })
  }

  const handleChangeCategory = (categoryId) => {
    const searchParams = new URLSearchParams()

    searchParams.append('engine_type', engineType)
    searchParams.append('engine_power', enginePower)
    searchParams.append('model', model)
    searchParams.append('year', year)
    searchParams.append('category', categoryId)

    window.parent.postMessage({
      'action': 'redirect',
      'data': {
        'url': '/diagram',
        'search': searchParams.toString()
      }
    }, '*')

    // navigate({
    //   pathname: '/diagram',
    //   search: searchParams.toString()
    // })
  }

  const buildProductTree = (productsArr, parentId = null, counter = { value: 0 }) => {
    const sortedProducts = productsArr
      .filter(product =>
        parentId === null
          ? !product.parent_id
          : product.parent_id && product.parent_id.toString() === parentId
      )
      .sort((a, b) => a.related_number - b.related_number)

    return sortedProducts.map((product) => {
      const currentValue = counter.value
      counter.value++

      const highlight = currentValue % 2 === 1
      const children = buildProductTree(productsArr, product._id.toString(), counter)

      return {
        ...product,
        highlight,
        children
      }
    })
  }

  const onChangeCategory = (categoryId) => {
    setSelectedCategory(categoryId)
    handleChangeCategory(categoryId)
  }

  const updateProductQuantity = (products, productId, action) => {
    return products.map(product => {
      if (product._id === productId) {
        if (action === 'increase') {
          return { ...product, qty: (product.qty || 1) + 1 }
        } else if (action === 'decrease' && product.qty > 1) {
          return { ...product, qty: product.qty - 1 }
        }
      }

      if (product.children && product.children.length > 0) {
        return {
          ...product,
          children: updateProductQuantity(product.children, productId, action)
        }
      }

      return product
    })
  }

  const handleIncreaseQty = (productId) => {
    setProducts(prevProducts => updateProductQuantity(prevProducts, productId, 'increase'))
  }

  const handleDecreaseQty = (productId) => {
    setProducts(prevProducts => updateProductQuantity(prevProducts, productId, 'decrease'))
  }

  const handleIncreaseDetailedProductQty = (productId) => {
    if (detailedProduct && detailedProduct._id === productId) {
      setDetailedProduct(prevValue => ({
        ...prevValue,
        qty: (prevValue.qty || 1) + 1
      }))
    }
  }

  const handleDecreaseDetailedProductQty = (productId) => {
    if (detailedProduct && detailedProduct._id === productId && detailedProduct.qty > 1) {
      setDetailedProduct(prevValue => ({
        ...prevValue,
        qty: prevValue.qty - 1
      }))
    }
  }

  const handleViewDetails = async (productId) => {
    const product = allProducts.find(product => product._id === productId)

    if (product) {
      setIsLoading(true)

      try {
        const response = await productApi.getById(product.shopify_id)

        if (response && response.success) {
          const data = response.data

          data.replacement_options = data.replacement_options
            .filter(product => product.attached_product)
            .map(product => ({ ...product, qty: 1 }))

          product.replacement_options = data.replacement_options

          setDetailedProduct(product)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleShowMoreLess = () => {
    if (expandedAll) {
      setDisplayLimit(10)
      setExpandedAll(false)
      setShowBottomGradient(true)
    } else {
      setDisplayLimit(prev => prev + 10)
      const totalCount = countAllProducts(products)
      if (displayLimit + 10 >= totalCount) {
        setExpandedAll(true)
        setShowBottomGradient(false)
      }
    }
  }

  const handleIncreaseDetailedProductReplacementOptionQty = (optionId) => {
    setDetailedProduct(prevProduct => ({
      ...prevProduct,
      replacement_options: prevProduct.replacement_options.map(
        option => option._id === optionId
          ? { ...option, qty: (option.qty || 1) + 1 }
          : option
      )
    }))
  }

  const handleDecreaseDetailedProductReplacementOptionQty = (optionId) => {
    setDetailedProduct(prevProduct => ({
      ...prevProduct,
      replacement_options: prevProduct.replacement_options.map(
        option => option._id === optionId && option.qty > 1
          ? { ...option, qty: option.qty - 1 }
          : option
      )
    }))
  }

  const handleIncreaseReplacementOptionQty = (optionId) => {
    setSelectedProductDetails(prevProductDetails => ({
      ...prevProductDetails,
      replacement_options: prevProductDetails.replacement_options.map(
        option => option._id === optionId
          ? { ...option, qty: (option.qty || 1) + 1 }
          : option
      )
    }))
  }

  const handleDecreaseReplacementOptionQty = (optionId) => {
    setSelectedProductDetails(prevProductDetails => ({
      ...prevProductDetails,
      replacement_options: prevProductDetails.replacement_options.map(
        option => option._id === optionId && option.qty > 1
          ? { ...option, qty: option.qty - 1 }
          : option
      )
    }))
  }

  const handleIncreaseRecommendedToolQty = (optionId) => {
    setSelectedProductDetails(prevProductDetails => ({
      ...prevProductDetails,
      recommended_tools: prevProductDetails.recommended_tools.map(
        option => option._id === optionId
          ? { ...option, qty: (option.qty || 1) + 1 }
          : option
      )
    }))
  }

  const handleDecreaseRecommendedToolQty = (optionId) => {
    setSelectedProductDetails(prevProductDetails => ({
      ...prevProductDetails,
      recommended_tools: prevProductDetails.recommended_tools.map(
        option => option._id === optionId && option.qty > 1
          ? { ...option, qty: option.qty - 1 }
          : option
      )
    }))
  }

  const fetchProduct = async (productId) => {
    setIsLoading(true)

    try {
      const response = await productApi.getById(productId)

      if (response && response.success) {
        const data = response.data

        data.replacement_options = data.replacement_options
          .filter(product => product.attached_product)
          .map(product => ({ ...product, qty: 1 }))

        data.recommended_tools = data.recommended_tools
          .filter(product => product.attached_product)
          .map(product => ({ ...product, qty: 1 }))

        setSelectedProductDetails(response.data)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePointSelect = (point) => {
    const product = allProducts.find(product => product.related_number === point.number)

    if (product) {
      setSelectedProduct(product)
    }
  }

  const countAllProducts = (products) => {
    let count = 0

    const countRecursive = (items) => {
      for (const item of items) {
        count++
        if (item.children && item.children.length > 0) {
          countRecursive(item.children)
        }
      }
    }

    countRecursive(products)
    return count
  }

  const findProductIndex = (productsList, targetId) => {
    let index = 0
    let found = false

    const traverse = (items) => {
      for (const item of items) {
        if (found) return

        if (item._id === targetId) {
          found = true
          return
        }

        index++

        if (item.children && item.children.length > 0) {
          traverse(item.children)
        }
      }
    }

    traverse(productsList)
    return found ? index : -1
  }

  useEffect(() => {
    if (category && category !== 'all') {
      fetchDiagram()
    } else {
      handleBack()
    }
  }, [category])

  useEffect(() => {
    window.parent.postMessage({
      'frameHeight': 1000
    }, "*")
  }, [])

  useEffect(() => {
    if (diagram) {
      window.parent.postMessage({
        'frameHeight': document.body.scrollHeight
      }, "*")
    }
  }, [diagram])

  useEffect(() => {
    const container = containerRef.current

    if (container) {
      handleScroll()

      container.addEventListener('scroll', handleScroll)

      return () => {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [products])

  useEffect(() => {
    if (selectedProduct && selectedProduct.shopify_id) {
      fetchProduct(selectedProduct.shopify_id)
    }
  }, [selectedProduct])

  useEffect(() => {
    if (selectedProductId && allProducts && allProducts.length > 0) {
      const product = allProducts.find(product => product._id === selectedProductId)

      if (product) {
        setSelectedProduct(product)

        const productIndex = findProductIndex(products, selectedProductId)

        if (window.innerWidth < 768) {
          if (productIndex >= displayLimit) {
            const newLimit = Math.ceil((productIndex + 1) / 10) * 10
            setDisplayLimit(newLimit)
          }
        } else {
          setTimeout(() => {
            const selectedElement = document.querySelector(`[data-product-id="${selectedProductId}"]`)

            if (selectedElement && containerRef.current) {
              const containerTop = containerRef.current.getBoundingClientRect().top
              const elementTop = selectedElement.getBoundingClientRect().top

              const scrollAmount = elementTop - containerTop

              containerRef.current.scrollTop += scrollAmount
            }
          }, 300)
        }
      }
    }
  }, [selectedProductId, allProducts])

  useEffect(() => {
    if (detailedProduct) {
      setIsDetailsModalOpen(true)
    } else {
      setIsDetailsModalOpen(false)
    }
  }, [detailedProduct])

  useEffect(() => {
    window.parent.postMessage({
      'frameHeight': document.body.scrollHeight
    }, "*")
  }, [selectedProductDetails])

  useEffect(() => {
    window.parent.postMessage({
      'frameHeight': document.body.scrollHeight
    }, "*")
  }, [displayLimit])

  return (
    diagram && (
      <div className='flex flex-col gap-[24px] my-4 mx-6 md:my-[30px] md:mx-[65px] max-w-[100vw]'>
        <div className='flex flex-col gap-4'>
          <div 
            className='text-[#005B75] text-base font-normal cursor-pointer underline' 
            onClick={handleBack}
          >
            {`< All ${diagramTitle} Diagrams`}
          </div>
          <div className='text-[#072743] text-3xl md:text-5xl title font-normal'>
            {diagram.category?.name} Diagram
          </div>
        </div>

        <Modal
          isOpen={isDetailsModalOpen}
          onClose={() => setDetailedProduct(null)}
          heading={window.innerWidth < 768 ? `${detailedProduct?.related_number ? `#${detailedProduct?.related_number} | ${detailedProduct?.sku}` : detailedProduct?.sku}` : 'REPLACEMENT OPTIONS'}
        >
          {detailedProduct && (
            <div className='flex flex-col gap-[20px]'>
              <div
                className='text-[#005B75] text-base font-normal cursor-pointer underline hidden md:block'
                onClick={() => setDetailedProduct(null)}
              >{`< Return to the ${diagramTitle} ${diagram.category?.name} Diagram`}</div>
              <DetailedProductBlock
                product={detailedProduct}
                onIncreaseQty={handleIncreaseDetailedProductQty}
                onDecreaseQty={handleDecreaseDetailedProductQty}
              />
              {detailedProduct.replacement_options.length > 0 && (
                <div className='flex flex-col gap-[20px]'>
                  <div className='h-[1px] bg-[#D9D9D9]'></div>
                  <div className='text-[#072743] text-[28px] title font-normal'>
                    Aftermarket Replacement Options For {detailedProduct.sku}
                  </div>
                  <div className='flex md:hidden flex-col gap-4'>
                    {detailedProduct.replacement_options.map(replacementOption => (
                      <DetailedReplacementOption
                        key={replacementOption._id}
                        product={replacementOption}
                        onIncreaseQty={handleIncreaseDetailedProductReplacementOptionQty}
                        onDecreaseQty={handleDecreaseDetailedProductReplacementOptionQty}
                      />
                    ))}
                  </div>
                  <div className='hidden md:block'>
                    <ScrollableCardContainer>
                      {detailedProduct.replacement_options.map(replacementOption => (
                        <div className='flex gap-[20px] w-[490px] flex-shrink-0 bg-[#F5F5F5] p-[25px]' key={replacementOption._id}>
                          <div className='flex flex-col gap-[10px] w-[175px]'>
                            <img src={replacementOption.attached_product?.image_url} className='w-full' />
                            {replacementOption.attached_product?.product_handle && (
                              <div className='flex mt-2 w-full'>
                                <a className='underline text-blue-900' href={`${process.env.REACT_APP_SHOP_ORIGIN}/products/${replacementOption.attached_product?.product_handle}`} target='_top' rel='noopener noreferrer'>
                                  View details
                                </a>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-[10px] flex-1">
                            <div className="text-base font-bold">{replacementOption.attached_product?.name}</div>
                            <div className="flex gap-2 items-center">
                              {replacementOption.attached_product?.regular_price && <div className="line-through">${formatPrice(replacementOption.attached_product?.regular_price)}</div>}
                              <div className="text-lg font-bold">
                                ${formatPrice(replacementOption.attached_product?.sale_price)} ea.
                              </div>
                            </div>
                            <div className="flex gap-[15px] items-center">
                              <QtyBlock
                                qty={replacementOption.qty}
                                onDecrease={(e) => {
                                  e.stopPropagation()
                                  handleDecreaseDetailedProductReplacementOptionQty(replacementOption._id)
                                }}
                                onIncrease={(e) => {
                                  e.stopPropagation()
                                  handleIncreaseDetailedProductReplacementOptionQty(replacementOption._id)
                                }}
                              />
                              <ShoppingCardBlock
                                onClick={(e) => {
                                  e.stopPropagation()
                                  window.parent.postMessage({
                                    'action': 'storeToBucket',
                                    'data': {
                                      'productId': replacementOption.attached_product?.shopify_id,
                                      'qty': replacementOption.qty
                                    }
                                  }, '*')
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </ScrollableCardContainer>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        <div className='flex flex-col xl:flex-row gap-10 items-start max-w-full'>
          <div className='w-full xl:w-[500px] flex-shrink-0 flex flex-col gap-4'>
            <div className='bg-[#4BAE4F]/10 p-5 flex flex-col gap-[20px] xl:hidden'>
              <div className='flex flex-wrap md:flex-nowrap gap-3 justify-between items-center text-sm'>
                <div className='flex items-center gap-2'>
                  <CheckImage />
                  <p>Applies to your <b>{diagramTitle}</b></p>
                </div>
                <div className='text-[#005B75] underline cursor-pointer' onClick={() => setIsDifferentEngineModalOpen(true)}>
                  Select different engine
                </div>
                <Modal
                  isOpen={isDifferentEngineModalOpen}
                  onClose={() => setIsDifferentEngineModalOpen(false)}
                  heading='SHOP PARTS BY ENGINE'
                >
                  <SearchPage showHeading={false} screenHeight={false} />
                </Modal>
              </div>
              <div className='bg-white'>
                <Select
                  options={categories.map(item => ({
                    label: item.name,
                    value: item._id
                  }))}
                  onChange={(value) => onChangeCategory(value)}
                  value={selectedCategory}
                />
              </div>
            </div>
            <ImageEditor
              points={points}
              image={image}
              selectedProduct={selectedProduct}
              onPointSelect={handlePointSelect}
              heading={`${diagramTitle} ${diagram.category?.name}`}
            />
          </div>
          <div className='flex flex-col gap-[60px] w-full'>
            <div className='flex flex-col gap-[22px]'>
              <div className='bg-[#4BAE4F]/10 p-5 flex flex-col gap-[20px] hidden xl:flex'>
                <div className='flex justify-between items-center text-sm'>
                  <div className='flex items-center gap-2'>
                    <CheckImage />
                    <p>Applies to your <b>{diagramTitle}</b></p>
                  </div>
                  <div className='text-[#005B75] underline cursor-pointer' onClick={() => setIsDifferentEngineModalOpen(true)}>
                    Select different engine
                  </div>
                  <Modal
                    isOpen={isDifferentEngineModalOpen}
                    onClose={() => setIsDifferentEngineModalOpen(false)}
                    heading='SHOP PARTS BY ENGINE'
                  >
                    <SearchPage showHeading={false} screenHeight={false} />
                  </Modal>
                </div>
                <div className='bg-white'>
                  <Select
                    options={categories.map(item => ({
                      label: item.name,
                      value: item._id
                    }))}
                    onChange={(value) => onChangeCategory(value)}
                    value={selectedCategory}
                  />
                </div>
              </div>

              <div className="relative">
                {showBottomGradient && (
                  <div className='absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none' />
                )}

                <div className="w-full bg-[#062743] text-white text-sm font-bold hidden md:flex">
                  <div className="text-left px-[20px] py-[5px] w-[170px] hidden md:block">#</div>
                  <div className="text-left px-[20px] py-[5px] flex-grow">Item</div>
                  <div className="text-left px-[20px] py-[5px] shrink-0 hidden md:block"></div>
                </div>

                <div
                  ref={containerRef}
                  className={`md:overflow-x-auto ${
                    window.innerWidth < 768 ? 'overflow-hidden' : 'max-h-[460px]'
                  }`}
                  style={{ 
                    maxHeight: window.innerWidth < 768 ? `${displayLimit * 230}px` : '460px' 
                  }}
                  onScroll={handleScroll}
                >
                  <div className="flex flex-col bg-white border border-[#D9D9D9]">
                    {products.map((product, index) => (
                      <ProductBlock
                        key={product._id}
                        index={index}
                        product={product}
                        selectedProduct={selectedProduct}
                        onSelect={setSelectedProduct}
                        onIncreaseQty={handleIncreaseQty}
                        onDecreaseQty={handleDecreaseQty}
                        onViewDetails={handleViewDetails}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="md:hidden mt-4 flex justify-center">
                {countAllProducts(products) > 10 && (
                  <Button 
                    text={expandedAll ? 'View Less' : 'View More'}
                    color='secondary'
                    onClick={handleShowMoreLess}
                  />
                )}
              </div>
              {selectedProductDetails && (
                <div className='flex flex-col gap-[20px] mt-5'>
                  {selectedProductDetails.replacement_options.length > 0 && (
                    <div className='flex flex-col gap-[20px] mt-5'>
                      <div className='text-[#072743] text-3xl md:text-4xl title font-normal'>
                        Aftermarket Replacement Options
                      </div>
                      <ScrollableCardContainer>
                        {selectedProductDetails.replacement_options.map(replacementOption => (
                          <div className='flex flex-col md:flex-row gap-[20px] w-[225px] md:w-[490px] flex-shrink-0 bg-[#F5F5F5] p-[25px] border border-[#D9D9D9] md:border-0' key={replacementOption._id}>
                            <div className='w-[175px]'>
                              <img src={replacementOption.attached_product?.image_url} className='w-full' />
                            </div>
                            <div className="flex flex-col gap-[10px] flex-1">
                              <div className='font-bold md:font-normal text-base'>{replacementOption.attached_product?.name}</div>
                              <div className="flex gap-2 items-center">
                                {replacementOption.attached_product?.regular_price && <div className="line-through">${formatPrice(replacementOption.attached_product?.regular_price)}</div>}
                                <div className="text-lg font-bold">
                                  ${formatPrice(replacementOption.attached_product?.sale_price)} ea.
                                </div>
                              </div>
                              <div className="hidden md:flex gap-[15px] items-center">
                                <QtyBlock
                                  qty={replacementOption.qty}
                                  onDecrease={(e) => {
                                    e.stopPropagation()
                                    handleDecreaseReplacementOptionQty(replacementOption._id)
                                  }}
                                  onIncrease={(e) => {
                                    e.stopPropagation()
                                    handleIncreaseReplacementOptionQty(replacementOption._id)
                                  }}
                                />
                                <ShoppingCardBlock
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    window.parent.postMessage({
                                      'action': 'storeToBucket',
                                      'data': {
                                        'productId': replacementOption.attached_product?.shopify_id,
                                        'qty': replacementOption.qty
                                      }
                                    }, '*')
                                  }}
                                />
                              </div>
                              <div className='block md:hidden'>
                                <Button
                                  text='Add to Cart'
                                  enableLoader
                                  onClick={() => {
                                    window.parent.postMessage({
                                      'action': 'storeToBucket',
                                      'data': {
                                        'productId': replacementOption.attached_product?.shopify_id,
                                        'qty': replacementOption.qty
                                      }
                                    }, '*')
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </ScrollableCardContainer>
                    </div>
                  )}
                  {selectedProductDetails.recommended_tools.length > 0 && (
                    <div className='flex flex-col gap-[20px] mt-5'>
                      <div className='text-[#072743] text-3xl md:text-4xl title font-normal'>
                        Recommended Tools
                      </div>
                      <ScrollableCardContainer>
                        {selectedProductDetails.recommended_tools.map(recommendedTool => (
                          <div className='flex flex-col md:flex-row gap-[20px] w-[225px] md:w-[490px] flex-shrink-0 bg-[#F5F5F5] p-[25px] border border-[#D9D9D9] md:border-0' key={recommendedTool._id}>
                            <div className='w-[175px]'>
                              <img src={recommendedTool.attached_product?.image_url} className='w-full' />
                            </div>
                            <div className="flex flex-col gap-[10px] flex-1">
                              <div className='font-bold md:font-normal text-base'>{recommendedTool.attached_product?.name}</div>
                              <div className="flex gap-2 items-center">
                                {recommendedTool.attached_product?.regular_price && <div className="line-through">${formatPrice(recommendedTool.attached_product?.regular_price)}</div>}
                                <div className="text-lg font-bold">
                                  ${formatPrice(recommendedTool.attached_product?.sale_price)} ea.
                                </div>
                              </div>
                              <div className="hidden md:flex gap-[15px] items-center">
                                <QtyBlock
                                  qty={recommendedTool.qty}
                                  onDecrease={(e) => {
                                    e.stopPropagation()
                                    handleDecreaseRecommendedToolQty(recommendedTool._id)
                                  }}
                                  onIncrease={(e) => {
                                    e.stopPropagation()
                                    handleIncreaseRecommendedToolQty(recommendedTool._id)
                                  }}
                                />
                                <ShoppingCardBlock
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    window.parent.postMessage({
                                      'action': 'storeToBucket',
                                      'data': {
                                        'productId': recommendedTool.attached_product?.shopify_id,
                                        'qty': recommendedTool.qty
                                      }
                                    }, '*')
                                  }}
                                />
                              </div>
                              <div className='block md:hidden'>
                                <Button
                                  text='Add to Cart'
                                  enableLoader
                                  onClick={() => {
                                    window.parent.postMessage({
                                      'action': 'storeToBucket',
                                      'data': {
                                        'productId': recommendedTool.attached_product?.shopify_id,
                                        'qty': recommendedTool.qty
                                      }
                                    }, '*')
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </ScrollableCardContainer>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  )
}

export default DiagramPage